import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { logAudit } from '../lib/auditLog';
import { isReaderCopy, matchesTwoTexts, standingText, wordCount } from '../lib/bookRowInfo';

const TONES = {
  gray:   { background: 'var(--brand-cream)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' },
  green:  { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' },
  amber:  { background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' },
  purple: { background: '#f3e8ff', color: '#6b21a8', border: '1px solid #d8b4fe' },
};

function Tag({ tone = 'gray', title, children }) {
  return (
    <span title={title} style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '3px 8px', borderRadius: 'var(--radius-10)', cursor: title ? 'help' : undefined, ...TONES[tone] }}>
      {children}
    </span>
  );
}

// Who wrote it and where it stands. `embedding` is { nodata: Set, stale: Set } of book ids, or null when
// those could not be read (then no embedding pill is shown rather than a wrong one).
export function BookPills({ book, embedding }) {
  const described = book.is_author_described === true;
  const standing = standingText(book);
  const claimant = book.claimant?.display_name || book.claimant?.email;
  return (
    <div data-book-pills style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--space-sm)' }}>
      {described
        ? <Tag tone="green" title="An author wrote the description, so the matchmaker can return this book.">author-described</Tag>
        : <Tag tone="gray" title="No author-written description. It is in the catalog and turns up in search, but the matchmaker cannot return it.">reference-only</Tag>}
      {isReaderCopy(book) && <Tag tone="purple" title="A reader wrote a description for this book. Nothing records which reader.">reader copy</Tag>}
      {book.entry_point && <Tag title="The door that created the book. It is not which door wrote the description.">added via {book.entry_point}</Tag>}
      {book.claimed_at && <Tag tone="purple">claimed{claimant ? ` by ${claimant}` : ''} · {new Date(book.claimed_at).toLocaleDateString()}</Tag>}
      {matchesTwoTexts(book) && <Tag tone="purple" title="The author's description and an archived reader description are joined into the one text this book is matched on.">matches on 2 texts</Tag>}
      {described && embedding?.nodata?.has(book.id) && <Tag tone="amber" title="Author-described, but the words have not been turned into numbers yet.">no matching data</Tag>}
      {described && embedding?.stale?.has(book.id) && <Tag tone="amber" title="The description changed after its numbers were made.">stale</Tag>}
      {standing && <Tag title={book.status === 'live' && book.live_at ? 'Counted from live_at, the same number the author sees on My Books.' : 'How long ago the record was created.'}>{standing}</Tag>}
    </div>
  );
}

function CopyBlock({ label, text }) {
  const n = wordCount(text);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', marginBottom: 3 }}>
        {label} · {n} {n === 1 ? 'word' : 'words'}
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-700)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{text}</div>
    </div>
  );
}

// The text is read when the row is opened, so the lists stay light.
export function DescriptionExpander({ bookId }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, error: null, row: null });

  async function toggle() {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setState({ loading: true, error: null, row: null });
    const { data, error } = await supabase
      .from('books').select('description, description_source, reader_description').eq('id', bookId).maybeSingle();
    if (error) { setState({ loading: false, error: error.message, row: null }); return; }
    setState({ loading: false, error: null, row: data });
  }

  const row = state.row;
  const main = (row?.description || '').trim();
  const archived = (row?.reader_description || '').trim();
  return (
    <div data-description style={{ marginBottom: 'var(--space-sm)' }}>
      <button type="button" onClick={toggle} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--neutral-600)', fontSize: 'var(--font-size-xs)', fontWeight: 700, textDecoration: 'underline', fontFamily: 'var(--font-body)' }}>
        {open ? 'Hide description' : 'Description'}
      </button>
      {open && (
        <div data-description-body style={{ marginTop: 6, padding: '10px 12px', background: 'var(--brand-paper)', border: '1px solid var(--brand-cream)', borderRadius: 'var(--radius-md)' }}>
          {state.loading && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>Loading…</span>}
          {state.error && <span role="alert" style={{ fontSize: 'var(--font-size-xs)', color: '#991b1b' }}>Couldn&apos;t read the description: {state.error}</span>}
          {row && !main && !archived && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>No description. 0 words.</span>}
          {row && main && <CopyBlock label={row.description_source === 'author' ? 'Description, written by the author' : row.description_source === 'reader' ? 'Description, written by a reader' : 'Description'} text={main} />}
          {row && archived && <CopyBlock label="Reader copy, archived beside the author's text" text={archived} />}
        </div>
      )}
    </div>
  );
}

// Fix author name (ticket 60): a real book entered badly, like The Fault in Our Stars under "Unknown",
// is not test data, so flagging it is the wrong repair. Writes books.author_name and nothing else.
// The audit line is written only after the database confirms the row changed.
export function FixAuthorName({ book, onFixed }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);

  function start() {
    setName(book.author_name || '');
    setNote(null);
    setOpen(true);
  }

  async function save() {
    const next = name.trim();
    const was = (book.author_name || '').trim();
    if (!next) { setNote({ bad: true, text: 'Type the author name first. It was not changed.' }); return; }
    if (next === was) { setNote({ bad: true, text: 'That is already the author name. Nothing was changed.' }); return; }
    setSaving(true); setNote(null);
    const { data, error } = await supabase.from('books').update({ author_name: next }).eq('id', book.id).select('id');
    setSaving(false);
    if (error) {
      // A code means the database answered and rolled the change back. No code means no answer at all.
      setNote({
        bad: true,
        text: error.code
          ? `Author name NOT changed, it is still "${was}": ${error.message}`
          : `No answer from the database, so the author name may or may not have changed. Reload the list to check. (${error.message || 'network error'})`,
      });
      return;
    }
    if (!data?.length) {
      setNote({ bad: true, text: `Author name NOT changed, it is still "${was}". The database refused it without saying why.` });
      return;
    }
    await logAudit('update', 'book', `Changed the author name of "${book.title}" from "${was}" to "${next}"`, book.id);
    setOpen(false);
    onFixed?.(next);
  }

  return (
    <>
      <button type="button" data-fix-author onClick={() => (open ? setOpen(false) : start())}
        style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' }}>
        {open ? 'Close author name' : 'Fix author name'}
      </button>
      {open && (
        <div data-fix-author-form style={{ flexBasis: '100%', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
          <input value={name} onChange={e => setName(e.target.value)} aria-label="Author name" disabled={saving}
            style={{ flex: '1 1 220px', maxWidth: 320, padding: '6px 10px', fontSize: 'var(--font-size-sm)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)' }} />
          <button type="button" data-fix-author-save onClick={save} disabled={saving}
            style={{ padding: '6px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--neutral-800)', background: 'var(--neutral-800)', color: 'var(--brand-white)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1, fontFamily: 'var(--font-body)' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {note && <div role="alert" data-fix-author-note style={{ flexBasis: '100%', fontSize: 'var(--font-size-sm)', color: '#991b1b' }}>{note.text}</div>}
        </div>
      )}
    </>
  );
}
